import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "../components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Compass, Plus, Crown, Users, Loader2, Sparkles, Lock, Globe } from "lucide-react";
import { toast } from "sonner";

export default function Spots() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [spots, setSpots] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const canHost = ["community_leader", "admin", "owner"].includes(user.role);

  const load = async () => { const { data } = await api.get("/spots"); setSpots(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try { const { data } = await api.post("/spots", form); setCreateOpen(false); setForm({ name: "", description: "" }); navigate(`/spots/${data.id}`); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const apply = async () => {
    try { await api.post("/leader/apply", { reason: "I'd love to host a community." }); toast.success("Application sent to the admins!"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const join = async (id) => {
    try { const { data } = await api.post(`/spots/${id}/join`); toast.success(data.status === "pending" ? "Request sent to the leader" : "Joined!"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (spots === null) return <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-spotter-maroon" /></div>;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-display text-3xl font-bold text-spotter-maroon flex items-center gap-2"><Compass className="w-7 h-7" />Hot Spots</h2>
        {canHost ? (
          <Button data-testid="create-spot-btn" onClick={() => setCreateOpen(true)} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Plus className="w-4 h-4" />Host a Hot Spot</Button>
        ) : (
          <Button data-testid="apply-leader-btn" onClick={apply} variant="outline" className="border-2 border-black bg-white gap-1"><Sparkles className="w-4 h-4 text-spotter-gold" />Apply to host Hot Spots</Button>
        )}
      </div>
      <p className="text-spotter-maroon mb-5">Communities for the pride. Join a Hot Spot or host your own.</p>

      {spots.length === 0 ? <p className="text-spotter-maroon">No Hot Spots yet. {canHost ? "Be the first to host one!" : "Apply to become a Community Leader and start one."}</p> : (
        <div className="grid sm:grid-cols-2 gap-4">
          {spots.map((s) => (
            <div key={s.id} className="bg-white border-2 border-black rounded-xl overflow-hidden brand-shadow-sm" data-testid={`spot-${s.id}`}>
              <div className="h-20 leopard-frame border-b-2 border-black" />
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <button onClick={() => navigate(`/spots/${s.id}`)} className="font-heading text-lg font-bold hover:underline text-left block">{s.name}</button>
                  <span data-testid={`spot-visibility-${s.id}`} className="shrink-0 flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full border-2 border-black bg-spotter-cream text-spotter-maroon">
                    {s.visibility === "private" ? <><Lock className="w-3 h-3" />Private</> : <><Globe className="w-3 h-3" />Public</>}
                  </span>
                </div>
                <p className="text-sm text-spotter-maroon line-clamp-2">{s.description}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-spotter-maroon">
                  <span className="flex items-center gap-1"><Crown className="w-3 h-3 text-spotter-gold" />{s.leader?.name}</span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" />{s.member_count}</span>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" data-testid={`view-spot-${s.id}`} onClick={() => navigate(`/spots/${s.id}`)} variant="outline" className="border-2 border-black flex-1">View</Button>
                  {!s.my_status && <Button size="sm" data-testid={`join-spot-${s.id}`} onClick={() => join(s.id)} className="bg-spotter-maroon text-spotter-cream border-2 border-black flex-1">{s.visibility === "private" ? "Request" : "Join"}</Button>}
                  {s.my_status === "pending" && <span className="text-xs self-center text-spotter-maroon flex-1 text-center">Pending</span>}
                  {s.my_status === "member" && <span className="text-xs self-center font-bold text-spotter-maroon flex-1 text-center">Member ✓</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="border-4 border-black bg-spotter-cream" data-testid="create-spot-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl text-spotter-maroon">Host a new Hot Spot</DialogTitle></DialogHeader>
          <Input data-testid="spot-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Spot name" className="border-2 border-black" />
          <Textarea data-testid="spot-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What's this community about?" className="border-2 border-black" />
          <div className="flex gap-2" data-testid="spot-visibility-picker">
            <button type="button" onClick={() => setForm({ ...form, visibility: "public" })} data-testid="spot-visibility-public"
              className={`flex-1 flex items-center gap-2 border-2 border-black rounded-lg p-2 text-left text-sm ${form.visibility === "public" ? "bg-spotter-maroon text-spotter-cream" : "bg-white text-spotter-maroon"}`}>
              <Globe className="w-4 h-4" /><span><b>Public</b> — anyone can join instantly</span>
            </button>
            <button type="button" onClick={() => setForm({ ...form, visibility: "private" })} data-testid="spot-visibility-private"
              className={`flex-1 flex items-center gap-2 border-2 border-black rounded-lg p-2 text-left text-sm ${form.visibility === "private" ? "bg-spotter-maroon text-spotter-cream" : "bg-white text-spotter-maroon"}`}>
              <Lock className="w-4 h-4" /><span><b>Private</b> — you approve each request</span>
            </button>
          </div>
          <DialogFooter><Button data-testid="spot-create-submit" onClick={create} className="bg-spotter-maroon text-spotter-cream border-2 border-black">Create Spot</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
